/**
 * BalanceSheetPanel — Accounts & Finance report.
 * Visible only to super_admin & chief_accountant (server also enforces).
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { FileDown, RefreshCw, Scale, CheckCircle2, AlertTriangle } from "lucide-react";
import { inr } from "@/lib/utils-format";

export default function BalanceSheetPanel() {
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/finance/balance-sheet", { params: { as_of: asOf } });
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Only Super Admin and Chief Accountant can view the Balance Sheet");
      else toast.error("Failed to load balance sheet");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const download = async (fmt) => {
    try {
      const r = await api.get("/finance/balance-sheet/export", { params: { as_of: asOf, format: fmt }, responseType: "blob" });
      const blob = new Blob([r.data], { type: fmt === "excel" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `balance-sheet-${asOf}.${fmt === "excel" ? "xlsx" : "pdf"}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  if (!data) return <div className="p-6 text-[#8B7355]">Loading balance sheet…</div>;
  const t = data.totals;

  const Section = ({ title, groups, total, tone }) => (
    <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden">
      <div className={`px-4 py-2 font-[Outfit] font-semibold text-white ${tone === "assets" ? "bg-emerald-600" : tone === "liabilities" ? "bg-red-600" : "bg-blue-600"}`}>
        {title}
      </div>
      <table className="w-full text-sm">
        <tbody>
          {groups.map((g, gi) => (
            <React.Fragment key={gi}>
              <tr className="bg-[#FAF6EE]">
                <td className="px-4 py-1.5 font-semibold text-[#2C1810]" colSpan={2}>{g.group}</td>
              </tr>
              {g.accounts.map((a) => (
                <tr key={a.code} className="border-t border-[#EAE5D9]">
                  <td className="px-8 py-1.5 text-xs"><span className="font-mono text-[#8B7355]">{a.code}</span> · {a.name}</td>
                  <td className="px-4 py-1.5 text-right font-mono">{inr(a.amount)}</td>
                </tr>
              ))}
              <tr className="border-t border-[#EAE5D9] bg-[#FDFBF7]">
                <td className="px-8 py-1.5 text-xs italic text-[#8B7355]">Subtotal — {g.group}</td>
                <td className="px-4 py-1.5 text-right font-mono italic">{inr(g.subtotal)}</td>
              </tr>
            </React.Fragment>
          ))}
          {groups.length === 0 && <tr><td className="px-4 py-4 text-center text-[#8B7355]" colSpan={2}>No {title.toLowerCase()} balances as of {data.as_of}.</td></tr>}
          <tr className="border-t-2 border-[#2C1810] bg-[#FEF9EA]">
            <td className="px-4 py-2 font-bold uppercase">Total {title}</td>
            <td className="px-4 py-2 text-right font-bold font-mono text-lg">{inr(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="pt-4 space-y-4" data-testid="balance-sheet-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-[Outfit] text-2xl font-semibold flex items-center gap-2">
            <Scale className="w-6 h-6 text-[#C05621]" /> Balance Sheet
          </h2>
          <p className="text-xs text-[#8B7355]">Auto-generated from ledger. Assets = Liabilities + Equity. Access restricted to Super Admin & Chief Accountant.</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">As of</Label><Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="w-40" data-testid="bs-as-of" /></div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="bs-refresh"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
          <Button variant="outline" onClick={() => download("pdf")} data-testid="bs-pdf"><FileDown className="w-3.5 h-3.5 mr-1" /> PDF</Button>
          <Button variant="outline" onClick={() => download("excel")} data-testid="bs-excel"><FileDown className="w-3.5 h-3.5 mr-1" /> Excel</Button>
        </div>
      </div>

      <div className={`rounded-lg border p-3 flex items-center justify-between ${t.balanced ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid="bs-status">
        <div className="flex items-center gap-2">
          {t.balanced ? <CheckCircle2 className="w-5 h-5 text-emerald-700" /> : <AlertTriangle className="w-5 h-5 text-red-700" />}
          <div>
            <div className={`font-[Outfit] font-semibold ${t.balanced ? "text-emerald-800" : "text-red-800"}`}>{t.balanced ? "Books are BALANCED ✓" : "Books are UNBALANCED"}</div>
            <div className="text-xs">
              Total Assets <b>₹{inr(t.assets)}</b> · Total Liabilities + Equity <b>₹{inr(t.liabilities_plus_equity)}</b>
              {!t.balanced && <> · Difference <b className="text-red-800">₹{inr(t.difference)}</b></>}
            </div>
          </div>
        </div>
        <Badge className={`border-0 ${t.balanced ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}>as of {data.as_of}</Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Assets" groups={data.assets} total={t.assets} tone="assets" />
        <div className="space-y-4">
          <Section title="Liabilities" groups={data.liabilities} total={t.liabilities} tone="liabilities" />
          <Section title="Equity" groups={data.equity} total={t.equity} tone="equity" />
        </div>
      </div>
    </div>
  );
}
